import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, RotateCcw, History, AlertTriangle, Sparkles } from "lucide-react";

const TIERS = [
  ["exact_high", "Exact match — High demand", [18, 30]],
  ["exact_medium", "Exact match — Medium demand", [12, 18]],
  ["phrase", "Phrase match", [10, 18]],
  ["broad", "Broad match testing", [6, 12]],
  ["competitor_asin", "Competitor ASIN targeting", [8, 16]],
  ["defensive", "Defensive brand keywords", [4, 8]],
  ["product_gap", "Product gap (do not bid aggressively)", [0, 0]],
];

const WEIGHTS = [
  ["demand", "Demand"],
  ["relevance", "Relevance"],
  ["business_fit", "Business fit"],
  ["competition", "Competition"],
];
const DEFAULT_WEIGHTS = { demand: 0.35, relevance: 0.35, business_fit: 0.20, competition: 0.10 };

export default function Settings() {
  const { user } = useAuth();
  const { selectedBrand, refreshBrands } = useBrands();
  const [bidRanges, setBidRanges] = useState({});
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);

  const seedFromBrand = (brand) => {
    const seed = {};
    for (const [k, , def] of TIERS) {
      const cur = brand?.bid_ranges?.[k];
      seed[k] = cur && cur.length === 2 ? { low: cur[0], high: cur[1] } : { low: def[0], high: def[1] };
    }
    setBidRanges(seed);
    setWeights({ ...DEFAULT_WEIGHTS, ...(brand?.opportunity_weights || {}) });
  };

  useEffect(() => { seedFromBrand(selectedBrand); /* eslint-disable-next-line */ }, [selectedBrand]);

  const loadAudit = () => {
    if (!selectedBrand) { setAudit([]); return; }
    api.get(`/brands/${selectedBrand.id}/audit`)
      .then((r) => setAudit(r.data || []))
      .catch(() => setAudit([]));
  };
  useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [selectedBrand]);

  const canEdit = ["admin", "brand_manager"].includes(user?.role);

  const setRange = (k, side, v) => setBidRanges((r) => ({ ...r, [k]: { ...r[k], [side]: Number(v) || 0 } }));
  const setWeight = (k, v) => setWeights((w) => ({ ...w, [k]: parseFloat(v) || 0 }));

  // Validation: low must be ≤ high for each tier
  const invalidTiers = Object.entries(bidRanges)
    .filter(([, v]) => Number(v.low) > Number(v.high))
    .map(([k]) => k);
  const totalW = Object.values(weights).reduce((a, b) => a + (b || 0), 0);
  const weightsValid = totalW > 0;

  const save = async ({ regenerate }) => {
    if (!selectedBrand) return;
    if (invalidTiers.length) {
      toast.error(`Fix invalid tiers (low > high): ${invalidTiers.join(", ")}`);
      return;
    }
    if (!weightsValid) {
      toast.error("Total weight must be greater than 0");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...selectedBrand,
        bid_ranges: Object.fromEntries(Object.entries(bidRanges).map(([k, v]) => [k, [v.low, v.high]])),
        opportunity_weights: weights,
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      await api.put(`/brands/${selectedBrand.id}`, payload);
      await refreshBrands();
      if (regenerate) {
        try {
          await api.post(`/recommendations/regenerate?brand_id=${selectedBrand.id}`);
          toast.success("Settings saved & recommendations regenerated for the latest upload.");
        } catch (e) {
          toast.warning("Settings saved, but regenerate failed — try the Reports page.");
        }
      } else {
        toast.success("Settings saved — re-run Regenerate on the latest upload to apply.");
      }
      loadAudit();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    const seed = {};
    for (const [k, , def] of TIERS) seed[k] = { low: def[0], high: def[1] };
    setBidRanges(seed);
    setWeights(DEFAULT_WEIGHTS);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Workspace</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Settings</h1>
          <p className="mt-1 text-sm text-slate-500">Configure bid ranges and opportunity score weights for <span className="font-semibold text-slate-900">{selectedBrand?.brand_name || "current brand"}</span>.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="settings-reset" onClick={resetDefaults} className="gap-2"><RotateCcw className="h-3.5 w-3.5" /> Reset defaults</Button>
            <Button data-testid="settings-save" onClick={() => save({ regenerate: false })} disabled={saving || !selectedBrand || !!invalidTiers.length} variant="outline" className="gap-2"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</Button>
            <Button data-testid="settings-save-regenerate" onClick={() => save({ regenerate: true })} disabled={saving || !selectedBrand || !!invalidTiers.length} className="bg-slate-900 text-white hover:bg-slate-800 gap-2"><Sparkles className="h-4 w-4" /> {saving ? "Saving…" : "Save & Regenerate"}</Button>
          </div>
        )}
      </div>

      {!!invalidTiers.length && (
        <div data-testid="settings-validation-error" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Invalid bid range — low cannot exceed high</div>
            <div className="text-xs mt-0.5">Fix these tiers before saving: {invalidTiers.join(", ")}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 tracking-tight">Profile</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Name</dt><dd className="text-slate-900 font-semibold">{user?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="text-slate-900">{user?.email}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Role</dt><dd className="text-slate-900 uppercase tracking-wider text-xs font-semibold">{user?.role?.replace("_", " ")}</dd></div>
          </dl>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 tracking-tight">Defaults</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Default marketplace</dt><dd className="text-slate-900">{selectedBrand?.marketplace || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Default country</dt><dd className="text-slate-900">{selectedBrand?.country || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Bid currency</dt><dd className="text-slate-900">INR (₹)</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Target ACOS</dt><dd className="text-slate-900">{selectedBrand?.target_acos || 0}%</dd></div>
          </dl>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900 tracking-tight">Bid range overrides (₹)</h3>
        <p className="mt-1 text-xs text-slate-500">Override per tier. Leave at default to use platform-wide defaults. Changes apply on next Regenerate.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bid-ranges">
          {TIERS.map(([key, label, def]) => {
            const invalid = invalidTiers.includes(key);
            return (
              <div key={key} className={`rounded-lg border p-3 ${invalid ? "border-rose-300 bg-rose-50/40" : "border-slate-200"}`}>
                <div className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                  <span>{label}</span>
                  {invalid && <span data-testid={`bid-${key}-error`} className="text-[10px] text-rose-700 font-bold uppercase tracking-wider">Low &gt; High</span>}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Default ₹{def[0]}–₹{def[1]}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Low</Label>
                    <Input data-testid={`bid-${key}-low`} disabled={!canEdit} type="number" value={bidRanges[key]?.low ?? def[0]} onChange={(e) => setRange(key, "low", e.target.value)} className={`h-8 ${invalid ? "border-rose-400" : ""}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">High</Label>
                    <Input data-testid={`bid-${key}-high`} disabled={!canEdit} type="number" value={bidRanges[key]?.high ?? def[1]} onChange={(e) => setRange(key, "high", e.target.value)} className={`h-8 ${invalid ? "border-rose-400" : ""}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900 tracking-tight">Opportunity score weights</h3>
        <p className="mt-1 text-xs text-slate-500">
          Weights are normalized at scoring time — they don't need to sum to 1. Each share below shows the effective contribution.
        </p>

        {/* Stacked normalized bar */}
        <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full border border-slate-200" data-testid="weights-bar">
          {WEIGHTS.map(([k], i) => {
            const pct = totalW > 0 ? ((weights[k] || 0) / totalW) * 100 : 0;
            const colors = ["bg-emerald-500", "bg-blue-500", "bg-violet-500", "bg-amber-500"];
            return <div key={k} style={{ width: `${pct}%` }} className={colors[i]} title={`${k}: ${pct.toFixed(0)}%`} />;
          })}
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="weights">
          {WEIGHTS.map(([k, l], i) => {
            const pct = totalW > 0 ? ((weights[k] || 0) / totalW) * 100 : 0;
            const colors = ["text-emerald-700", "text-blue-700", "text-violet-700", "text-amber-700"];
            return (
              <div key={k} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">{l}</Label>
                  <span data-testid={`weight-${k}-pct`} className={`text-[11px] font-bold ${colors[i]}`}>{pct.toFixed(0)}%</span>
                </div>
                <Input data-testid={`weight-${k}`} disabled={!canEdit} type="number" step="0.05" min="0" value={weights[k] ?? 0} onChange={(e) => setWeight(k, e.target.value)} className="h-8 mt-1" />
                <div className="mt-1 text-[10px] text-slate-400">default {DEFAULT_WEIGHTS[k]} · raw {(weights[k] ?? 0).toFixed(2)}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[11px] text-slate-500">Total raw weight: <span className="font-semibold text-slate-700">{totalW.toFixed(2)}</span></div>
      </div>

      {/* Audit log */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="settings-audit">
        <button
          data-testid="audit-toggle"
          onClick={() => setAuditOpen((v) => !v)}
          className="w-full flex items-center justify-between px-6 py-4 text-left"
        >
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900 tracking-tight">Settings change log</h3>
            <span className="text-[11px] text-slate-400">({audit.length} {audit.length === 1 ? "entry" : "entries"})</span>
          </div>
          <span className="text-xs text-slate-500">{auditOpen ? "Hide" : "Show"}</span>
        </button>
        {auditOpen && (
          <div className="border-t border-slate-200 px-6 py-4">
            {!audit.length && <div className="text-sm text-slate-500">No tracked changes yet for this brand.</div>}
            {!!audit.length && (
              <div className="space-y-2">
                {audit.map((a) => (
                  <div key={a.id} className="rounded-lg border border-slate-100 p-3 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-900">{a.changed_by_name || "—"}</div>
                      <div className="text-slate-500">{(a.changed_at || "").replace("T", " ").slice(0, 16)} UTC</div>
                    </div>
                    <ul className="mt-1.5 space-y-1">
                      {(a.diffs || []).map((d, i) => (
                        <li key={i} className="text-slate-700">
                          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider mr-1.5">{d.field}</span>
                          changed
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
